import styled from '@emotion/styled'
import ChannelManageHeader from '@/components/channels/verify/Header'
import { Button, Flex, Icon, Text, Title } from '@collable/ui'
import ChannelVerifyMethodItem from '@/components/channels/verify/MethodItem'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import pagePath from '@/utils/pagePath'

type Method = 'google' | 'email'

const BUTTON_TEXT_MAP: Record<Method, string> = {
  google: '구글 계정으로 인증하기',
  email: '유튜브 이메일로 인증하기',
}

const GOOGLE_VERIFY_URL = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${import.meta.env.VITE_GOOGLE_CLIENT_ID}&redirect_uri=${import.meta.env.VITE_GOOGLE_VERIFY_REDIRECT_URL}&response_type=code&prompt=select_account&scope=https://www.googleapis.com/auth/youtube.readonly`

const ChannelVerifyPage = () => {
  const [selectedMethod, setSelectedMethod] = useState<Method>('google')
  const navigate = useNavigate()

  const onClickVerify = () => {
    if (selectedMethod === 'google') {
      window.location.href = GOOGLE_VERIFY_URL
      return
    }

    navigate(pagePath.verifyChannelEmail)
  }

  return (
    <Container direction="column" gap={40} alignItems="center">
      <ChannelManageHeader />
      <Content direction="column" gap={40}>
        <Flex direction="column" gap={8}>
          <Title size="title2" fontWeight="bold" color="labelStrong" as="h1">
            채널 인증 방법
          </Title>
          <Text size="body2" color="labelAlternative">
            원하는 인증 방식을 선택하세요
          </Text>
        </Flex>
        <Flex direction="column" gap={12}>
          <ChannelVerifyMethodItem
            title="구글 계정으로 인증하기"
            description="유튜브 채널 소유권을 빠르게 인증할 수 있어요"
            tag="인증 속도 빠름"
            isSelected={selectedMethod === 'google'}
            onSelect={() => setSelectedMethod('google')}
            additionalInfo={
              <AdditionalInfo direction="column" gap={8}>
                <Flex alignItems="center" gap={4}>
                  <Icon name="checkCircle" iconSize={20} iconColor="primaryNormal" />
                  <Text size="body3" fontWeight="bold" color="primaryNormal">
                    안심하세요
                  </Text>
                </Flex>
                <Text size="body3" color="labelNeutral">
                  채널 인증에 필요한 정보만 확인해요. 수익이나 민감한 정보는 저희도 볼 수 없어요.
                </Text>
              </AdditionalInfo>
            }
          />
          <ChannelVerifyMethodItem
            title="유튜브 이메일로 인증하기"
            description="유튜브 채널에 기재된 이메일이 본인 이메일인지 확인해요"
            isSelected={selectedMethod === 'email'}
            onSelect={() => setSelectedMethod('email')}
          />
        </Flex>
        <Button width="100" color="primary" kind="solid" size="large" onClick={onClickVerify}>
          {BUTTON_TEXT_MAP[selectedMethod]}
        </Button>
      </Content>
    </Container>
  )
}

export default ChannelVerifyPage

const Container = styled(Flex)`
  min-width: 100vh;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.background.backgroundAlternative};
`

const Content = styled(Flex)`
  width: 600px;
  border-radius: 20px;
  padding: 28px;
  background-color: ${({ theme }) => theme.common.commonWhite};
`

const AdditionalInfo = styled(Flex)`
  width: 100%;
  padding: 20px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.fill.fillAssistive};
`
